import { detectSolidBackground } from '../core/background-detector.js';
import { detectBakedCheckerboard } from '../core/checkerboard-detector.js';
import { parsePng } from '../core/png-parser.js';
import { analyzePixelsAsync, classifyAnalysis } from '../core/pixel-analyzer.js';
import type { AnalysisResult } from '../core/types.js';

interface PendingAnalysis {
  readonly resolve: (result: AnalysisResult) => void;
  readonly reject: (error: Error) => void;
  readonly onProgress?: (progress: number) => void;
  readonly file: File;
  readonly fallbackBuffer: ArrayBuffer;
  readonly analysisId: string;
}

type WorkerResponse =
  | { readonly type: 'progress'; readonly requestId: string; readonly progress: number }
  | { readonly type: 'result'; readonly requestId: string; readonly result: AnalysisResult }
  | {
      readonly type: 'error';
      readonly requestId: string;
      readonly error: { readonly name: string; readonly message: string; readonly code?: string };
    };

const pending = new Map<string, PendingAnalysis>();
let worker: Worker | null = null;

export async function analyzeFile(
  file: File,
  onProgress?: (progress: number) => void,
): Promise<AnalysisResult> {
  const buffer = await file.arrayBuffer();
  const analysisId = makeId();
  if (!supportsWorkerDecode()) {
    return analyzeOnMainThread(file, buffer, analysisId, onProgress);
  }

  const requestId = makeId();
  return new Promise<AnalysisResult>((resolve, reject) => {
    const task: PendingAnalysis = {
      resolve,
      reject,
      onProgress,
      file,
      fallbackBuffer: buffer.slice(0),
      analysisId,
    };
    pending.set(requestId, task);
    try {
      const activeWorker = getWorker();
      activeWorker.postMessage(
        {
          type: 'analyze',
          requestId,
          analysisId,
          buffer,
          file: {
            name: file.name,
            size: file.size,
            type: file.type,
            lastModified: file.lastModified,
          },
        },
        [buffer],
      );
    } catch {
      pending.delete(requestId);
      fallbackToMainThread(task);
    }
  });
}

export async function trimFile(
  record: { readonly file: File; readonly analysis: AnalysisResult },
  margin: 0 | 2 | 4 | 8 | 16,
): Promise<Blob> {
  const box = record.analysis.pixels.contentBoundingBox;
  if (!box) throw new Error('This image has no visible content to trim.');

  const width = record.analysis.png.width;
  const height = record.analysis.png.height;
  const x = Math.max(0, box.x - margin);
  const y = Math.max(0, box.y - margin);
  const right = Math.min(width, box.x + box.width + margin);
  const bottom = Math.min(height, box.y + box.height + margin);
  const outputWidth = right - x;
  const outputHeight = bottom - y;

  if (x === 0 && y === 0 && outputWidth === width && outputHeight === height) {
    throw new Error('This image has no transparent padding to trim at the selected margin.');
  }

  const bitmap = await createImageBitmap(record.file);
  const canvas = document.createElement('canvas');
  canvas.width = outputWidth;
  canvas.height = outputHeight;
  const context = canvas.getContext('2d');
  if (!context) {
    bitmap.close();
    throw new Error('The browser could not create a canvas for the trimmed image.');
  }
  context.clearRect(0, 0, outputWidth, outputHeight);
  context.drawImage(bitmap, x, y, outputWidth, outputHeight, 0, 0, outputWidth, outputHeight);
  bitmap.close();

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('The browser could not encode the trimmed PNG.'));
    }, 'image/png');
  });
}

export function disposeAnalyzer(): void {
  worker?.terminate();
  worker = null;
  for (const task of pending.values()) task.reject(new Error('Image analysis was cancelled.'));
  pending.clear();
}

async function analyzeOnMainThread(
  file: File,
  buffer: ArrayBuffer,
  analysisId: string,
  onProgress?: (progress: number) => void,
): Promise<AnalysisResult> {
  const started = performance.now();
  const reportProgress = (progress: number): void => onProgress?.(Math.round(progress * 100));
  reportProgress(0.05);
  const png = parsePng(buffer);
  reportProgress(0.16);
  const bitmap = await createImageBitmap(new Blob([buffer], { type: 'image/png' }));
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('The browser could not create a 2D analysis canvas.');
  context.drawImage(bitmap, 0, 0);
  bitmap.close();
  const image = context.getImageData(0, 0, canvas.width, canvas.height);
  reportProgress(0.42);
  const pixels = await analyzePixelsAsync(image, {
    onProgress: (progress) => reportProgress(0.42 + progress * 0.34),
  });
  const solidBackground = detectSolidBackground(image);
  const checkerboard = detectBakedCheckerboard(image);
  const labels = classifyAnalysis(png, pixels, { solidBackground, checkerboard });
  reportProgress(1);
  return {
    id: analysisId,
    file: {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      width: png.width,
      height: png.height,
    },
    png,
    pixels,
    labels,
    solidBackground,
    checkerboard,
    analyzedAt: new Date().toISOString(),
    durationMs: performance.now() - started,
  };
}

function getWorker(): Worker {
  if (worker) return worker;
  worker = new Worker(new URL('./analyze.worker.ts', import.meta.url), { type: 'module' });
  worker.addEventListener('message', handleWorkerMessage);
  worker.addEventListener('error', () => {
    const tasks = [...pending.values()];
    pending.clear();
    worker?.terminate();
    worker = null;
    for (const task of tasks) fallbackToMainThread(task);
  });
  return worker;
}

function handleWorkerMessage(event: MessageEvent<WorkerResponse>): void {
  const message = event.data;
  const task = pending.get(message.requestId);
  if (!task) return;

  if (message.type === 'progress') {
    task.onProgress?.(Math.round(message.progress * 100));
    return;
  }

  pending.delete(message.requestId);
  if (message.type === 'result') {
    task.resolve(message.result);
    return;
  }

  const error = new Error(message.error.message);
  error.name = message.error.name;
  if (message.error.code) Object.assign(error, { code: message.error.code });

  if (message.error.name === 'WorkerDecodeUnavailableError') {
    fallbackToMainThread(task);
    return;
  }

  task.reject(error);
}

function fallbackToMainThread(task: PendingAnalysis): void {
  void analyzeOnMainThread(task.file, task.fallbackBuffer, task.analysisId, task.onProgress).then(
    task.resolve,
    task.reject,
  );
}

function supportsWorkerDecode(): boolean {
  return (
    typeof Worker !== 'undefined' &&
    typeof OffscreenCanvas !== 'undefined' &&
    typeof createImageBitmap !== 'undefined'
  );
}

function makeId(): string {
  return typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}
